/**
 * Bookmark collection API helpers
 *
 * A `Collection` is named as a `Bookmark` in the API, which contains a list of `BookmarkItem`.
 * We use the term `Collection` to avoid potential confusion.
 */

import { getBaseCursorPagingArgs } from 'src/components/CursorPaging/cursorPagingUtils'
import { getNodesFromConnection } from 'src/utils/graphql'
import {
  executeDeleteBookmark,
  executeUpdateBookmarkItem,
  executeUpsertBookmark,
  fetchGetBookmark,
  fetchListUserBookmarks,
  useListUserBookmarks,
} from '../apollo/bookmark'
import type {
  BookmarkItem,
  UpsertBookmarkInput,
} from '../apollo/generated/graphql'

/**
 * The name of the default collection
 *
 * The default collection is a special collection
 * that is created automatically and cannot be renamed or removed.
 * Currently, it has no special functionality compared to normal collections,
 * which I think is a mistake.
 */
const DEFAULT_COLLECTION_NAME = '$$default$$'

export function isDefaultCollection(name: string | undefined) {
  return name === DEFAULT_COLLECTION_NAME
}

/**
 * Create a collection
 */
export async function createCollection(input: UpsertBookmarkInput) {
  const { data } = await executeUpsertBookmark({ input })
  return data?.upsertBookmark
}

/**
 * Update a collection
 *
 * For the default collection (named `$$default$$`), only a few fields can be updated.
 */
export async function updateCollection(
  bookmarkId: string,
  input: UpsertBookmarkInput,
) {
  const { data } = await executeUpsertBookmark({
    id: bookmarkId,
    input,
  })
  return data?.upsertBookmark
}

/**
 * Remove a collection
 */
export async function removeCollection(bookmarkId: string) {
  await executeDeleteBookmark({ id: bookmarkId })
}

/**
 * Fetch the info of a collection
 * @param bookmarkId
 */
export async function fetchCollection(bookmarkId: string) {
  const { data } = await fetchGetBookmark({ id: bookmarkId })
  return data?.bookmark
}

const PAGE_SIZE = 20

/**
 * Fetch the collection list of a user
 */
export async function listCollections({
  userId,
  username,
  before,
  after,
  pageSize = PAGE_SIZE,
  type,
}: {
  userId?: string
  username?: string
  before?: string
  after?: string
  pageSize?: number
  type?: string
}) {
  const { data } = await fetchListUserBookmarks({
    userId,
    username,
    type,
    ...getBaseCursorPagingArgs({ before, after, pageSize }),
  })
  return {
    collections: getNodesFromConnection(data?.user?.bookmarks).reverse(),
    pageInfo: data?.user?.bookmarks?.pageInfo,
  }
}

/**
 * Hook to fetch the collection list of a user
 */
export function useCollections({
  userId,
  username,
  before,
  after,
  pageSize = PAGE_SIZE,
  type = '',
}: {
  userId?: string
  username?: string
  before?: string
  after?: string
  pageSize?: number
  type?: string
}) {
  const { data, loading, refetch } = useListUserBookmarks({
    userId,
    username,
    type,
    ...getBaseCursorPagingArgs({ before, after, pageSize }),
  })
  return {
    collections: getNodesFromConnection(data).reverse(),
    pageInfo: data?.pageInfo,
    loading,
    reload: refetch,
  }
}

export async function addCollectionItem(
  bookmarkId: string,
  itemInfo: { artworkId: string } | { modelId: string },
) {
  await executeUpdateBookmarkItem({ input: { bookmarkId, ...itemInfo } })
}

export async function removeCollectionItem(
  bookmarkId: string,
  itemInfo: { artworkId: string } | { modelId: string },
) {
  await executeUpdateBookmarkItem({
    input: { bookmarkId, ...itemInfo, remove: true },
  })
}

export declare function listCollectionItems(
  bookmarkId: string,
): Promise<BookmarkItem[]>
